import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

const colorClasses = {
  primary: {
    bg: 'bg-blue-500',
    light: 'bg-blue-100',
    text: 'text-blue-600',
    border: 'border-l-blue-500',
  },
  success: {
    bg: 'bg-green-500',
    light: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-l-green-500',
  },
  warning: {
    bg: 'bg-yellow-500',
    light: 'bg-yellow-100',
    text: 'text-yellow-600',
    border: 'border-l-yellow-500',
  },
  danger: {
    bg: 'bg-red-500',
    light: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-l-red-500',
  },
  info: {
    bg: 'bg-cyan-500',
    light: 'bg-cyan-100',
    text: 'text-cyan-600',
    border: 'border-l-cyan-500',
  },
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  color = 'primary',
}) => {
  const { isDark } = useTheme();
  const colors = colorClasses[color];

  return (
    <div
      className={`rounded-lg shadow-sm border-l-4 ${colors.border} ${
        isDark ? 'bg-gray-800' : 'bg-white'
      } p-6 transition-all hover:shadow-md`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wide`}>
            {title}
          </p>
          <p className={`mt-2 text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
          {trend && (
            <div className="mt-2 flex items-center">
              <span
                className={`text-sm font-medium ${
                  trend.isPositive ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {trend.isPositive ? '+' : '-'}{Math.abs(trend.value)}%
              </span>
              <svg
                className={`w-4 h-4 ml-1 ${
                  trend.isPositive ? 'text-green-500' : 'text-red-500'
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                {trend.isPositive ? (
                  <path
                    fillRule="evenodd"
                    d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                )}
              </svg>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} ml-2`}>
                vs ontem
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${colors.light}`}>
          <div className={colors.text}>{icon}</div>
        </div>
      </div>
    </div>
  );
};

export default StatCard;
